import { Actions, CHANGE_ASSIGNMENT_STATUS, STORE_ASSIGNMENT, STORE_ASSIGNMENT_SERVICE_INSTANCES, STORE_HHGS, STORE_UNASSIGNED_ASSIGNMENTS } from '@k2/common/assignments-state/actions';
import { assignmentSchema } from '@k2/common/assignments-state/normalizer-schemas';
import { LoadedAssignmentsState, RootState, State } from '@k2/common/assignments-state/state';
import { getDenormalized, getOneDenormalized } from '@k2/common/entities-state/selectors';
import { Assignment, ServiceInstanceMapItem } from '@k2/common/entities-state/types';
import { ActionReducer, combineReducers, Selector } from '@ngrx/store';
import { identity, mergeDeepRight } from 'ramda';
import { createSelector } from 'reselect';

export function rootMetaReducerAssignments(
  reducer: ActionReducer<RootState>
): ActionReducer<RootState> {
  return (state: RootState | undefined, action: Actions): RootState => {
    let nextState = reducer(state, action);

    switch (action.type) {
      case CHANGE_ASSIGNMENT_STATUS:
        return mergeDeepRight(nextState, {
          entities: {
            assignments: {
              [action.assignmentId]: {
                status: action.newStatus
              }
            }
          }
        });

      case STORE_HHGS:
        return mergeDeepRight(nextState, {
          entities: {
            assignments: {
              [action.assignmentId]: {
                hhgs: action.hhgs
              }
            }
          }
        });

      case STORE_ASSIGNMENT_SERVICE_INSTANCES:
        const serviceInstancesMap: { [key: number]: ServiceInstanceMapItem } = {};

        action.assignment.workflows.forEach(workflow => {
          workflow.services.forEach(service => {
            serviceInstancesMap[service.id] = {
              name: service.name,
              label: service.label,
              serviceId: service.id,
              assignmentServiceInstanceId: service.assignmentServiceInstanceId
            };
          });
        });

        // also map non-HHG services present in HHG Workflow
        action.assignment?.hhg_workflow?.services.forEach(service => {
          if (service.type === 'SMD') {
            serviceInstancesMap[service.id] = {
              name: service.name,
              label: service.label,
              serviceId: service.id,
              assignmentServiceInstanceId: service.assignmentServiceInstanceId
            };
          }
        });

        return mergeDeepRight(nextState, {
          entities: {
            assignments: {
              [action.assignment.id]: {
                serviceInstances: serviceInstancesMap
              }
            }
          }
        });

      default:
        return nextState;
    }
  };
}

export const featureReducer = combineReducers<State>({
  loaded: loadedReducer,
  unassigned: unassignedReducer
});

export function loadedReducer(
  state: LoadedAssignmentsState = {},
  action: Actions
): LoadedAssignmentsState {
  if (action.type === STORE_ASSIGNMENT) {
    return { ...state, [action.id]: { loaded: true } };
  }
  return state;
}

export function getAssignment(id: number): Selector<RootState, Assignment> {
  return createSelector(
    [(state: RootState) => state.assignments.loaded[id], identity],
    (assignmentState, state: RootState) => {
      if (assignmentState != null && assignmentState.loaded)
        return getOneDenormalized(assignmentSchema, id)(state);
    }
  );
}

export function unassignedReducer(state: number[], action: Actions): number[] {
  if (action.type === STORE_UNASSIGNED_ASSIGNMENTS) {
    return action.ids;
  }
  return state;
}

export const getUnassignedAssignments: Selector<RootState, Assignment[]> = createSelector(
  [(state: RootState) => state.assignments.unassigned, identity],
  (ids, state) => getDenormalized(assignmentSchema, ids)(state)
);
